import DOMPurify from 'dompurify';

/**
 * The app's global reset (Tailwind preflight) gives every image `height: auto` and
 * `max-width: 100%`. A real picture keeps its shape that way, but an image whose file has a
 * different shape from its attributes does not: a 1×1 spacer GIF sized `width="400"
 * height="1"` renders 400×400. Giving a sized image the aspect ratio of its attributes keeps
 * that shape while it still shrinks to fit.
 *
 * Runs on the raw HTML before DOMPurify, so the sanitizer has the last word on what is
 * rendered. A <template> is inert: nothing in it loads or runs while it is parsed.
 */
export function keepSizedEmailImages(html: string): string {
    if (!html || !/<img/i.test(html)) return html;
    const template = document.createElement('template');
    template.innerHTML = html;
    template.content.querySelectorAll('img[width][height]').forEach((img) => {
        const width = (img.getAttribute('width') || '').trim();
        const height = (img.getAttribute('height') || '').trim();
        if (!/^\d+$/.test(width) || !/^\d+$/.test(height) || Number(width) === 0 || Number(height) === 0) return;
        const style = (img.getAttribute('style') || '').trim();
        // The sender's own height or aspect ratio wins.
        if (/(^|;)\s*(height|aspect-ratio)\s*:/i.test(style)) return;
        // Appended as text: jsdom's CSSStyleDeclaration drops `aspect-ratio`.
        const separator = style && !style.endsWith(';') ? '; ' : style ? ' ' : '';
        img.setAttribute('style', `${style}${separator}aspect-ratio: ${width} / ${height}`);
    });
    return template.innerHTML;
}

/**
 * Tags an email may not bring into the app. A `<style>` element applies to the whole page
 * (DOMPurify keeps one placed after body content, in a table, in SVG, or after an Outlook
 * `<o:p>`): it could repaint or hide the app, or `@import` a remote sheet. Form controls
 * would let an email show a working sign-in form; mail clients disable them too. Inline
 * `style` attributes stay, so the email keeps its own look inside its box.
 */
export const EMAIL_FORBID_TAGS = ['style', 'form', 'input', 'textarea', 'select', 'button'];

export function sanitizeEmailHtml(raw: string): string {
    return DOMPurify.sanitize(keepSizedEmailImages(raw), { FORBID_TAGS: EMAIL_FORBID_TAGS });
}
